import React from "react"
import { useContext, useState, useEffect } from "react"
import { useQuery, useLazyQuery } from "@apollo/client"
import _ from "lodash"
import { DateTime } from "luxon"
import { UserContext } from "../../context/userContext"
import { useParams, useNavigate, Link } from "react-router-dom"
import AppBreadcrumb from "../../components/AppBreadcrumb/AppBreadcrumb"
import customRecoveryStyles from "../../styles/recoveryPlan/RecoveryPlan.module.css"
import CustomTable from "../../components/CustomTable/CustomTable"
import { Button } from "primereact/button"
// Loader
import { LoaderMedium } from "../../components/Loaders"

// Queries
import { GET_USER, GET_SAVED_BLOCKS_BY_ORGANIZATION_ID } from "./queries"

export default function ViewRecoveryPlan() {
  const [blockSaved, setBlockSaved] = useState({
    strengths_interests: "",
    hopes_dreams: "",
  })
  const userCtx = useContext(UserContext)
  const participant_id = userCtx.participant_id
  const [nameStaff, setNameStaff] = useState("-")
  const [nameStaffUpdated, setNameStaffUpdated] = useState("-")
  const { organization_id, staff_id, organization_name } = useParams()
  const block_name = "session__recovery_plan"
  const navigate = useNavigate()

  const { loading } = useQuery(GET_SAVED_BLOCKS_BY_ORGANIZATION_ID, {
    fetchPolicy: "no-cache",
    variables: {
      participant_id,
      organization_id,
      block_name: block_name,
    },
    onCompleted: (data) => {
      const dataParse = data.blockSavedbyOrganizationID
        ? JSON.parse(data.blockSavedbyOrganizationID.data)
        : {}
      setBlockSaved(dataParse)
      getStaffUpdate({
        variables: {
          staff_id: staff_id,
          organization_id: organization_id,
        },
      })
    },
  })

  const [getStaffCreated] = useLazyQuery(GET_USER, {
    fetchPolicy: "no-cache",
    variables: {
      staff_id: blockSaved?.staff_id,
      organization_id: blockSaved?.organization_id,
    },
    onCompleted: (data) => {
      const staffFullName = data.user.staff_name_first + " " + data.user.staff_name_last
      setNameStaff(staffFullName)
    },
  })

  const [getStaffUpdate] = useLazyQuery(GET_USER, {
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      setNameStaffUpdated(data.user.staff_name_first + " " + data.user.staff_name_last)
    },
  })

  const handlePrint = () => {
    const printWindow = window.open("", "", "height=800,width=1000")
    if (!printWindow) return

    const sanitizeHTML = (html) => {
      const div = document.createElement("div")
      div.innerHTML = html
      return div.textContent || div.innerText || ""
    }

    const formatSafeDate = (date) => {
      if (!date || isNaN(new Date(date).getTime())) return "Not set"
      return formatDate(new Date(date))
    }

    const strengths =
      blockSaved?.values?.strengths_interests
        ?.split("</p><p>")
        .map((p) => `<p>${sanitizeHTML(p)}</p>`)
        .join("") || ""

    const hopes =
      blockSaved?.values?.hopes_dreams
        ?.split("</p><p>")
        .map((p) => `<p>${sanitizeHTML(p)}</p>`)
        .join("") || ""

    const changeThing =
      blockSaved?.values?.change_thing
        ?.split("</p><p>")
        .map((p) => `<p>${sanitizeHTML(p)}</p>`)
        .join("") || ""

    const complementary = sanitizeHTML(
      blockSaved?.values?.plan_complementary || ""
    )

    const peopleRows =
      mapDataSupportivePeople()
        ?.map(
          (p) =>
            `<tr>
        <td>${p.name}</td>
        <td>${p.relationship}</td>
        <td>${p.contact}</td>
        <td>${p.phone}</td>
        <td>${p.email}</td>
      </tr>`
        )
        .join("") || ""

    const goalsRows =
      blockSaved?.values?.plan_goals
        ?.map(
          (g) =>
            `<tr>
        <td>${sanitizeHTML(g.goal)}</td>
        <td>${g.domain}</td>
        <td>${g.status}</td>
        <td>${g.priority_level}</td>
      </tr>`
        )
        .join("") || ""

    printWindow.document.write(`<!DOCTYPE html>
      <html>
        <head>
          <title>Recovery Plan</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 2rem; color: #333; }
            h1, h2 { margin-top: 2rem; border-bottom: 1px solid #ccc; padding-bottom: .5rem; }
            p { margin: 0.5rem 0; }
            table { width: 100%; border-collapse: collapse; margin-top: 1rem; }
            th { background-color: #f2f2f2; }
            th, td { border: 1px solid #aaa; padding: 0.5rem; text-align: left; vertical-align: top; }
            tr:nth-child(even) { background-color: #f9f9f9; }
          </style>
        </head>
        <body>
          <h1>Recovery Plan</h1>
  
          <h2>Basic Info</h2>
          <p><strong>Organization:</strong> ${organization_name}</p>
          <p><strong>Participant:</strong> ${userCtx.participant_name_first + ' ' + userCtx.participant_name_last}</p>
    
          <p><strong>Date Plan Created:</strong> ${formatSafeDate(
      blockSaved?.values?.date_created
    )}</p>
          <p><strong>Date Last Updated:</strong> ${formatSafeDate(
      blockSaved?.values?.date_changed || blockSaved?.values?.session_date
    )}</p>
          <p><strong>Created By:</strong> ${nameStaff ?? ""}</p>
          <p><strong>Updated By:</strong> ${nameStaffUpdated ?? ""}</p>
  
          <h2>Strengths & Interests</h2>
          ${strengths}
  
          <h2>Hopes & Dreams</h2>
          ${hopes}
  
          <h2>Change One Thing</h2>
          ${changeThing}
  
          <h2>Complementary Plan</h2>
          <p>${complementary}</p>
  
          <h2>Supportive People</h2>
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Relationship</th>
                <th>Okay to Contact</th>
                <th>Phone</th>
                <th>Email</th>
              </tr>
            </thead>
            <tbody>
              ${peopleRows}
            </tbody>
          </table>
  
          <h2>Recovery Plan Goals</h2>
          <table>
            <thead>
              <tr>
                <th>Goal</th>
                <th>Domain</th>
                <th>Status</th>
                <th>Priority</th>
              </tr>
            </thead>
            <tbody>
              ${goalsRows}
            </tbody>
          </table>
  
          <h2>Plan Details</h2>
          <p><strong>Review Frequency:</strong> ${blockSaved?.values?.plan_review_frequency || "Not set"
      }</p>
        </body>
      </html>`)

    printWindow.document.close()
    printWindow.focus()
    printWindow.print()
    printWindow.close()
  }

  useEffect(() => {
    if (blockSaved?.staff_id && blockSaved?.organization_id) {
      getStaffCreated()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockSaved, userCtx.participant_id])

  const formatDate = (value) => {
    if (!value) return ""
    return DateTime.fromJSDate(value).toLocal().toFormat("MM/dd/yyyy")
  }

  const mapOrganization = (organization) => {
    if (!organization) {
      return "Unknown Organization"
    }
    if (organization === false) {
      return "Unknown Organization"
    }
    return organization
  }

  const mapDataSupportivePeople = () => {
    return blockSaved?.values?.supportive_people?.map((d) => {
      return {
        name: d.name || "-",
        relationship: d.relationship || "-",
        contact: d.contact ? "Yes" : "No",
        phone: d.support_phone || "-",
        email: d.email || "-"
      }
    })
  }

  const PriorityTemplate = (data) => {
    let className = ""
    switch (data) {
      case "High":
        className = customRecoveryStyles.highPriority
        break
      case "Medium":
        className = customRecoveryStyles.mediumPriority
        break
      case "Low":
        className = customRecoveryStyles.lowPriority
        break
      default:
    }
    return (
      <div className={`${customRecoveryStyles.tag} ${className}`}>{data}</div>
    )
  }

  const StatusTemplate = (data) => {
    let className = ""
    switch (data) {
      case "Active":
        className = customRecoveryStyles.active
        break
      case "Inactive":
        className = customRecoveryStyles.inactive
        break
      case "In the future":
        className = customRecoveryStyles.future
        break
      default:
    }
    return (
      <div className={`${customRecoveryStyles.tag} ${className}`}>{data}</div>
    )
  }

  const mapDataRecoveryPlan = () => {
    return blockSaved?.values?.plan_goals?.map((d) => {
      return {
        goal: d.goal.replace(/<\/?(?:p|span|ul|li)[^>]*>/g, "") || "-",
        domain: d.domain || "-",
        status: StatusTemplate(d.status) || "-",
        priority: PriorityTemplate(d.priority_level) || "-",
      }
    })
  }

  const headersRecoveryPlan = () => {
    return [
      {
        value: "goal",
        label: "Active Goals",
        size: 3,
      },
      {
        value: "domain",
        label: "Recovery Domain",
        size: 3,
      },
      {
        value: "status",
        label: "Status",
        size: 3,
      },
      {
        value: "priority",
        label: "Priority",
        size: 2,
      },
    ]
  }

  const headersSupportivePeople = () => {
    return [
      {
        value: "name",
        label: "Name",
        size: 2,
      },
      {
        value: "relationship",
        label: "Relationship",
        size: 2,
      },
      {
        value: "contact",
        label: "Okay to contact?",
        size: 3,
      },
      {
        value: "phone",
        label: "Phone",
        size: 3,
      },
      {
        value: "email",
        label: "Email",
        size: 2,
      },
    ]
  }

  const sortingRecoveryPlan = () => {
    return [
      {
        value: "sortBy",
        label: "",
        options: [
          { name: "Goal", code: "goal" },
          { name: "Domain", code: "domain" },
          { name: "Status", code: "status" },
          { name: "Priority", code: "priority" },
        ],
        default: "goal",
        placeholder: "Sort by",
        showClear: true,
        type: "multiselect",
      },
    ]
  }

  if (loading) return <LoaderMedium />

  return (
    <div>
      <div className="main-content">
        <AppBreadcrumb
          items={[
            {
              template: (
                <Link
                  to="/recovery-plan"
                  onClick={(e) => {
                    e.preventDefault()
                    navigate(-1)
                  }}
                  style={{
                    color: "#873CAA",
                    fontSize: "16px",
                    lineHeight: "21.79px",
                    fontWeight: "400",
                    textDecoration: "none",
                    cursor: "pointer",
                  }}
                >
                  Recovery Plan
                </Link>
              ),
            },
            { label: "View" },
          ]}
        />
        <div className="w-full">
          <div
            className="w-full "
            style={{
              display: "flex",
              justifyContent: "space-between",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <h1
              className={customRecoveryStyles.title}
              style={{ marginBottom: "0px", marginTop: "0px" }}
            >
              Recovery Plan
            </h1>
            <Button
              className={customRecoveryStyles.iconRecoveryPlanview}
              icon="pi pi-print"
              onClick={handlePrint}
            />
          </div>
          <hr className="line" />
          <div className={customRecoveryStyles.viewDataBlockRecoveryPlan}>
            <div className={customRecoveryStyles.viewOrgBlockItem}>
              <span>Organization</span>
              <span className={customRecoveryStyles.viewOrgValue}>
                {mapOrganization(organization_name)}
              </span>
            </div>
            <div className={customRecoveryStyles.viewOrgBlockItem}>
              <span>Date Plan Created</span>
              <span className={customRecoveryStyles.viewOrgValue}>
                {formatDate(new Date(blockSaved?.values?.date_created)) || "-"}
              </span>
            </div>
            <div className={customRecoveryStyles.viewOrgBlockItem}>
              <span>Created by</span>
              <span className={customRecoveryStyles.viewOrgValue}>
                {!_.isNull(blockSaved) ? nameStaff : "-"}
              </span>
            </div>
            <div className={customRecoveryStyles.viewOrgBlockItem}>
              <span>Date Last Updated</span>
              <span className={customRecoveryStyles.viewOrgValue}>
                {blockSaved?.values?.date_changed === "not set"
                  ? formatDate(new Date(blockSaved?.values?.date_changed) || "-")
                  : formatDate(new Date(blockSaved?.values?.session_date)) || "-"}
              </span>
            </div>
            <div className={customRecoveryStyles.viewOrgBlockItem}>
              <span>Last Updated By</span>
              <span className={customRecoveryStyles.viewOrgValue}>
                {nameStaffUpdated || "-"}
              </span>
            </div>
          </div>
        </div>

        <div className={customRecoveryStyles.viewDataBlock}>
          <div className={customRecoveryStyles.viewDataBlockItemLabel}>
            Strengths and Interests
          </div>
          {blockSaved?.values?.strengths_interests
            .split("</p><p>")
            .map((paragraph, index) => (
              <div
                key={index}
                className={customRecoveryStyles.viewDataBlockItemValue}
                dangerouslySetInnerHTML={{
                  __html: paragraph.replace(/<\/?p>/g, "") || "-",
                }}
              />
            ))}
          <div className={customRecoveryStyles.viewDataBlockItemLabel}>
            Hopes & Dream
          </div>
          {blockSaved?.values?.hopes_dreams
            .split("</p><p>")
            .map((paragraph, index) => (
              <div
                key={index}
                className={customRecoveryStyles.viewDataBlockItemValue}
                dangerouslySetInnerHTML={{
                  __html: paragraph.replace(/<\/?p>/g, "") || "-",
                }}
              />
            ))}
          <div className={customRecoveryStyles.viewDataBlockItemLabel}>
            Change one thing?
          </div>
          {blockSaved?.values?.change_thing
            .split("</p><p>")
            .map((paragraph, index) => (
              <div
                key={index}
                className={customRecoveryStyles.viewDataBlockItemValue}
                dangerouslySetInnerHTML={{
                  __html: paragraph.replace(/<\/?p>/g, "") || "-",
                }}
              />
            ))}
          <div className={customRecoveryStyles.viewDataBlockItemLabel}>
            Complementary Plan
          </div>
          <div
            className={customRecoveryStyles.viewDataBlockItemValue}
            dangerouslySetInnerHTML={{
              __html: blockSaved?.values?.plan_complementary || "-",
            }}
          ></div>
        </div>

        <div className={customRecoveryStyles.viewDataBlockPeople}>
          <span
            style={{
              marginBottom: "20px",
              marginTop: "10px",
              color: "#383838",
              fontSize: "16px",
              fontWeight: "700",
            }}
          >
            Supportive People
          </span>
          <CustomTable
            headers={headersSupportivePeople()}
            collection={mapDataSupportivePeople()}
            styles={{ marginTop: "0px" }}
          />
        </div>

        <div className={customRecoveryStyles.viewDataBlockPeople}>
          <CustomTable
            headers={headersRecoveryPlan()}
            collection={mapDataRecoveryPlan()}
            styles={{ marginTop: "0px" }}
            sorting={sortingRecoveryPlan()}
            title={"Recovery Plan Goals"}
          />
        </div>

        <div className={customRecoveryStyles.viewDataBlockPeople}>
          <span
            style={{
              marginBottom: "20px",
              marginTop: "10px",
              color: "#383838",
              fontSize: "16px",
              fontWeight: "700",
            }}
          >
            Plan Details
          </span>
          <div
            className={customRecoveryStyles.viewDataBlockItemLabel}
            style={{ marginBottom: "8px" }}
          >
            Expected frequency of supervision?
          </div>
          <div className={customRecoveryStyles.viewDataBlockItemValue}>
            {blockSaved?.values?.plan_review_frequency || "-"}
          </div>

          {blockSaved?.values?.plan_review_frequency === "Other" && (
            <div style={{ marginTop: "20px" }}>
              <div
                className={customRecoveryStyles.viewDataBlockItemLabel}
                style={{ marginBottom: "8px" }}
              >
                What frequency will be used?
              </div>
              <div className={customRecoveryStyles.viewDataBlockItemValue}>
                {blockSaved?.values?.plan_review_other || "-"}
              </div>
            </div>
          )}
        </div>

        {/* Add some padding to the bottom of the form */}
        <div style={{ height: "10px" }} />
      </div>
    </div>
  )
}
